import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { getSelfCheckinCardReader, postSelfCheckinCardReaderWithCheckin, selfCheckinFetchCardKeyRooms } from '@/apis/aipass'
import { Trans, useTranslation } from 'react-i18next'
import { RoomToIssueKeyModal } from '@/components/organisms/self-checkin/room-to-issue-key-modal'
import Lottie from 'react-lottie'
import { CardReaderCommand, RoomInfoType, SelfCheckinCardReaderType } from '@/models/self-checkin/card-reader'
import { selfCardPCIDStorageKey, serialNumberStorageKey } from '@/constants/self-checkin-device-storage'
import dayjs from 'dayjs'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { FrontTabletButton } from '@/components/molecules/front-tablet/button'
import { ButtonType } from '@/components/atoms/button'
import { reportError } from '@/components/apps/error-boundary'
import { createSelfCheckinResponsiveStyle, FrontTabletResponsiveToken } from '@/utils/front-tablet-responsive-style'

export type SelfCheckinIssueRoomKeyState = {
  hotelId: string
  checkinId: string
}

export const SelfCheckinIssueRoomKey: React.FC<{}> = () => {
  const history = useHistory()
  const { errorHandler } = useErrorHandler()
  const { t } = useTranslation()
  const [isShowRoomToIssueKeyModal, setIsShowRoomToIssueKeyModal] = useState(false)
  const [isShowRoomToIssueKeyFailedModal, setIsShowRoomToIssueKeyFailedModal] = useState(false)
  const [latestIssueCommandReceive, setLatestIssueCommandReceive] = useState<SelfCheckinCardReaderType>()
  const [assignRooms, setAssignRooms] = useState<RoomInfoType[]>([])
  const [numberOfCards, setNumberOfCards] = useState<number>(1)
  const [currentIssueRoomIndex, setCurrentIssueRoomIndex] = useState<number>(0)
  const [processId, setProcessId] = useState<string>()
  const [issuedCardNum, setIssuedCardNum] = useState<number>(0)
  const [isExpiredDisplayOnComplete, setIsExpiredDisplayOnComplete] = useState(false)
  const [isCardIssueSuccess, setIsCardIssueSuccess] = useState(false)
  const [standbyNextIssueNum, setStandbyNextIssue] = useState<number>()
  const [isAllIssueCompleted, setIsAllIssueCompleted] = useState(false)
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()

  const animationData = require('@/static/images/json/loading.json')

  const initPageStatus = async () => {
    if (!stateErrorHandling(state, 'setting')) {
      return
    }
    if (!isEnabledIssueKey() || state.completeIssueRoomKey) {
      goToNextPage()
      return
    }
    const assignRooms = await selfCheckinFetchCardKeyRooms({
      hotelId: state.hotelId,
      reservationIdWithAssignIds: state.complete!.selectedReservationAndRooms,
    })
    if (!assignRooms?.length) {
      goToNextPage()
      return
    }
    setCurrentIssueRoomIndex(0)
    setAssignRooms(assignRooms)
    setIsShowRoomToIssueKeyModal(true)
  }

  const isEnabledIssueKey = () => {
    const storedSerialNumber = localStorage.getItem(serialNumberStorageKey) || ''
    return storedSerialNumber.length > 0
  }

  const subscribeIssueResult = (processId: string) => {
    const intervalId = setInterval(async () => {
      if (!localStorage.getItem(serialNumberStorageKey)) {
        return
      }
      const res = await getSelfCheckinCardReader(
        state.hotelId,
        localStorage.getItem(serialNumberStorageKey)!,
        localStorage.getItem(selfCardPCIDStorageKey),
        processId,
      )
      if (!res?.length) {
        return
      }
      if (!res[0].is_success) {
        clearInterval(intervalId)
        alert('カード発行機に接続できませんでした')
        console.error('カード発行機に接続できませんでした')
        goToNextPage()
        return
      }

      const removalMessage = res.find(r => r.command === CardReaderCommand.CardIssueRemoval)
      if (removalMessage) {
        if (!hasAdditionalRoomCard && !hasNextIssueRoom) {
          setIsAllIssueCompleted(true)
        }
        setIssuedCardNum(prev => prev + 1)
        setIsCardIssueSuccess(true)
        setTimeout(() => {
          setIsExpiredDisplayOnComplete(true)
        }, 3000)
      }

      const completeMessage = res.find(r => r.command === CardReaderCommand.CardIssueComplete)
      if (completeMessage) {
        clearInterval(intervalId)
      }

      setLatestIssueCommandReceive(completeMessage || removalMessage || res[0])
    }, 500)
    return intervalId
  }

  const sendIssueRequest = async (branchNumber: number) => {
    try {
      setStandbyNextIssue(undefined)
      setIsExpiredDisplayOnComplete(false)
      setIsCardIssueSuccess(false)
      const { processId } = await postSelfCheckinCardReaderWithCheckin(
        state.hotelId,
        localStorage.getItem(serialNumberStorageKey)!,
        localStorage.getItem(selfCardPCIDStorageKey),
        CardReaderCommand.CardIssueRequest,
        {
          guestRoomAssignId: assignRooms[currentIssueRoomIndex].guestRoomAssignId,
          checkinId: state.complete!.checkinId,
          checkinDate: dayjs(assignRooms[currentIssueRoomIndex].assignCheckinDate).format('YYYY-MM-DD HH:mm'),
          checkoutDate: dayjs(assignRooms[currentIssueRoomIndex].assignCheckoutDate).format('YYYY-MM-DD HH:mm'),
          roomNumber: Number(assignRooms[currentIssueRoomIndex].roomNumber),
          numberOfCards: assignRooms[currentIssueRoomIndex].requiredCardCount,
          branchNumber,
        },
      )
      setProcessId(processId)
    } catch (e) {
      errorHandler(e)
      reportError(e)
      goToNextPage()
    }
  }

  const makeIssueAnnounceTemplate = () => {
    if (isCardIssueSuccess && (!isExpiredDisplayOnComplete || isAllIssueCompleted)) {
      return (
        <div className="operation-text highlight">
          <img className="check-image" src={require('@/static/images/self-checkin/icon_check_border.svg')} />
          <div>{t('SelfCheckIn.RoomKeyIssued')}</div>
        </div>
      )
    }
    if (latestIssueCommandReceive?.command === CardReaderCommand.CardIssueWriting) {
      return (
        <div className="operation-text highlight">
          <Lottie height="80px" width="80px" options={{ animationData, autoplay: true, loop: true }} />
          <div>{t('SelfCheckIn.RoomKeyBeingIssued')}</div>
        </div>
      )
    }
    return (
      <div className="operation-text">
        <Trans i18nKey={issuedCardNum === 0 ? 'SelfCheckIn.OnIcReader' : 'SelfCheckIn.OnNextCard'} />
      </div>
    )
  }

  const goToNextPage = () => {
    saveState({ ...state, completeIssueRoomKey: true })
    history.replace({ pathname: `/self-checkin/complete` })
  }

  const hasAdditionalRoomCard = useMemo(() => {
    return numberOfCards < assignRooms[currentIssueRoomIndex]?.requiredCardCount
  }, [currentIssueRoomIndex, numberOfCards, assignRooms])

  const hasNextIssueRoom = useMemo(() => currentIssueRoomIndex + 1 < assignRooms.length, [currentIssueRoomIndex, assignRooms])

  useEffect(() => {
    if (!isExpiredDisplayOnComplete) {
      return
    }
    if (hasAdditionalRoomCard) {
      console.log('部屋に対する追加発行')
      setNumberOfCards(numberOfCards + 1)
      setStandbyNextIssue(numberOfCards + 1)
    } else if (hasNextIssueRoom) {
      console.log('次の部屋に対する新規発行')
      setNumberOfCards(1)
      setCurrentIssueRoomIndex(currentIssueRoomIndex + 1)
      setIsShowRoomToIssueKeyModal(true)
    } else {
      console.log('発行完了')
      goToNextPage()
    }
  }, [isExpiredDisplayOnComplete])

  useEffect(() => {
    const isPreviousNotCompleted = latestIssueCommandReceive && latestIssueCommandReceive.command !== CardReaderCommand.CardIssueComplete
    if (!standbyNextIssueNum || isPreviousNotCompleted) {
      return
    }
    let ignore = false
    setTimeout(() => {
      if (!ignore) {
        sendIssueRequest(standbyNextIssueNum)
      }
    }, 1000)
    return () => {
      ignore = true
    }
  }, [standbyNextIssueNum, latestIssueCommandReceive])

  useEffect(() => {
    if (!processId) {
      return
    }
    const intervalId = subscribeIssueResult(processId)
    return () => clearInterval(intervalId)
  }, [processId])

  useEffect(() => {
    window.scrollTo(0, 0)
    initPageStatus()
  }, [])

  return assignRooms.length ? (
    <SelfCheckinLayout state={state} header={{ title: t('SelfCheckIn.IssueRoomKey') }}>
      <div css={componentStyle}>
        <div className="announce-block">
          <div className="room-number-text">{assignRooms[currentIssueRoomIndex]?.roomNumber}</div>
          <div className="issue-announcement-text">
            <Trans i18nKey="SelfCheckIn.IssueRoomKeyProcess" components={{ span: <span /> }} values={{ n: numberOfCards }} />
          </div>
          {makeIssueAnnounceTemplate()}
          <div className="cannot-issue-text" onClick={() => setIsShowRoomToIssueKeyFailedModal(!isShowRoomToIssueKeyFailedModal)}>
            {t('SelfCheckIn.CannotIssuedRoomKey')}
          </div>
          {isShowRoomToIssueKeyFailedModal && (
            <div className="cannot-issue-text-detail">
              <div>
                <Trans i18nKey={'SelfCheckIn.FrontDeskCardKeyIssueErrorDesc'} />
              </div>
              <FrontTabletButton
                buttonType={ButtonType.Secondary}
                customCss={issueRoomKeyButtonStyle}
                onClick={() => {
                  goToNextPage()
                }}
              >
                {t('SelfCheckIn.Confirmed')}
              </FrontTabletButton>
            </div>
          )}
        </div>
        <div className="card-reader-image-block">
          <img src={require('@/static/images/self-checkin/checkin-card-reader-a.png')} />
          <img src={require('@/static/images/self-checkin/checkin-card-reader-b.png')} />
        </div>
      </div>
      {isShowRoomToIssueKeyModal && (
        <RoomToIssueKeyModal
          roomNumber={assignRooms[currentIssueRoomIndex].roomNumber}
          totalNumber={assignRooms[currentIssueRoomIndex].requiredCardCount}
          onClose={async () => {
            setIsShowRoomToIssueKeyModal(false)
            setStandbyNextIssue(1)
          }}
        />
      )}
    </SelfCheckinLayout>
  ) : (
    <></>
  )
}

const issueRoomKeyButtonStyle = css(
  createSelfCheckinResponsiveStyle({
    landscape11: { fontSize: FrontTabletResponsiveToken.standardButtonFontSize.landscape11 },
    portrait: { fontSize: FrontTabletResponsiveToken.standardButtonFontSize.portrait },
    landscape13: { fontSize: FrontTabletResponsiveToken.standardButtonFontSize.landscape13 },
  }),
)

const componentStyle = css(
  {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 653,
    padding: '40px 40px 0 121px',
    display: 'grid',
    gridTemplateColumns: '311px 469px',
    columnGap: 121,
    alignItems: 'start',
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    lineHeight: 1.5,
    '*': { fontFamily: 'Noto Sans JP' },
    '.announce-block': {
      paddingTop: 104,
      textAlign: 'center',
      // 1行に収まらない部屋番号でも行送りが間延びしないよう、行の高さはminHeightで担保して行間は倍率で持たせる
      '.room-number-text': {
        minHeight: 82,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 60,
        lineHeight: 1.2,
        fontWeight: 'bold',
        letterSpacing: 3,
        color: '#F2A40B',
        wordBreak: 'break-word',
      },
      '.issue-announcement-text': {
        minHeight: 29,
        fontSize: 21,
        lineHeight: '29px',
        fontWeight: 'bold',
        letterSpacing: 1.05,
        color: '#272727',
        span: {
          color: '#F2A40B',
        },
      },
      '.operation-text': {
        marginTop: 40,
        boxSizing: 'border-box',
        minHeight: 174,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 21,
        lineHeight: '29px',
        fontWeight: 'bold',
        letterSpacing: 1.05,
        color: '#676767',
        borderTop: '2px solid #CCCCCC',
        borderBottom: '2px solid #CCCCCC',
        wordBreak: 'break-word',
        '&.highlight': {
          color: '#F2A40B',
        },

        '.check-image': {
          borderRadius: '50%',
          border: '3px solid #F2A40B',
          width: 70,
          height: 70,
          padding: 12,
        },
      },
      '.cannot-issue-text': {
        marginTop: 50,
        fontSize: 16,
        color: '#676767',
        borderBottom: 'solid 1px #676767',
        width: 'max-content',
      },
      '.cannot-issue-text-detail': {
        width: '100%',
        marginTop: 3,
        textAlign: 'left',
        '>div': {
          padding: '16px 0',
          wordBreak: 'keep-all',
        },
      },
    },
    '.card-reader-image-block': {
      position: 'relative',
      width: 469,
      height: 563,
      '> img': {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: 0,
        animation: 'image-switch-animation 10s infinite',
        '&:nth-of-type(1)': { animationDelay: '0s' },
        '&:nth-of-type(2)': { animationDelay: '5s' },
      },
    },
    '@keyframes image-switch-animation': {
      '0%': { opacity: 0 },
      '5%': { opacity: 1 },
      '60%': { opacity: 1 },
      '65%': { opacity: 0 },
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      minHeight: 513,
      padding: '40px 40px 0 49px',
      gridTemplateColumns: '311px 361px',
      columnGap: 59,
      borderRadius: 0,
      '.announce-block': {
        paddingTop: 54,
      },
      '.card-reader-image-block': {
        width: 361,
        height: 433,
      },
    },
    landscape13: {
      minHeight: 754,
      maxWidth: 1230,
      margin: '0 auto',
      padding: '40px 40px 0 139px',
      gridTemplateColumns: '341px 562px',
      columnGap: 148,
      '.announce-block': {
        paddingTop: 140,
        '.room-number-text': {
          minHeight: 87,
          fontSize: 64,
          letterSpacing: 3.2,
        },
        '.issue-announcement-text': {
          minHeight: 34,
          fontSize: 25,
          lineHeight: '34px',
          letterSpacing: 1.25,
        },
        '.operation-text': {
          minHeight: 188,
          marginTop: 32,
          fontSize: 25,
          lineHeight: '34px',
          letterSpacing: 1.25,
        },
        '.cannot-issue-text': { marginTop: 34, fontSize: 20 },
      },
      '.card-reader-image-block': {
        width: 562,
        height: 674,
      },
    },
  }),
)
